import type { FC } from 'react';

import { DEFAULT_TURN_DURATION_MS } from '@skillarea/game-chess';

import type { TGamePlayer } from '../types';

import { ChessGame } from './ChessGame';
import styles from './ChessGame.module.scss';
import type { IChessGameResult } from './types';
import { useChessLocalGame } from './use-chess-local-game';

import { useUserAvatar } from '~/stores/userStore';
import { playLoseSound, playWinSound } from '~/lib/game-sounds';

interface IChessGameLocalProps {
  player1: TGamePlayer;
  player2: TGamePlayer;
  turnDurationMs?: number;
  enableAI?: boolean;
  aiDifficulty?: number;
  rules: string;
  showTestPromotionButton?: boolean;
  onGameEnd?: (result: IChessGameResult) => void;
  onSurrender?: () => void;
}

export const ChessGameLocal: FC<IChessGameLocalProps> = ({
  aiDifficulty,
  enableAI = true,
  onGameEnd: onGameEndProp,
  onSurrender,
  player1,
  player2,
  rules,
  showTestPromotionButton = false,
  turnDurationMs = DEFAULT_TURN_DURATION_MS,
}) => {
  const avatarUrl = useUserAvatar();
  const handleGameEnd = (result: IChessGameResult) => {
    if (!result.isDraw && result.winner) {
      if (result.winner === 'w') playWinSound();
      else playLoseSound();
    }
    onGameEndProp?.(result);
  };

  const game = useChessLocalGame({
    turnDurationMs,
    enableAI,
    aiDifficulty,
    onGameEnd: handleGameEnd,
    onSurrender,
  });

  const content = (
    <ChessGame
      fen={game.fen}
      isGameOver={game.isGameOver}
      onMove={game.handleMove}
      onSurrender={game.handleSurrender}
      player1={{ ...player1, avatarUrl: player1.avatarUrl ?? avatarUrl }}
      player1TimeLeftMs={game.whiteTimeMs}
      player2={player2}
      player2TimeLeftMs={game.blackTimeMs}
      playerColor="w"
      rulesContent={rules}
      turnDurationMs={turnDurationMs}
    />
  );

  if (showTestPromotionButton) {
    return (
      <div className={styles.localRoot}>
        <div className={styles.testButtons}>
          <button
            className={styles.testPromotionButton}
            onClick={game.loadTestPromotionPosition}
            type="button"
          >
            Test: pawn promotion
          </button>
          <button
            className={styles.testPromotionButton}
            onClick={game.loadTestEnPassantPosition}
            type="button"
          >
            Test: en passant
          </button>
        </div>
        {content}
      </div>
    );
  }

  return content;
};
